import React, { useEffect, useState, useRef } from "react";
import { Box, Button, Typography, Paper, Slide, Stack } from "@mui/material";
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { Routes, Route, useLocation } from "react-router-dom";
import { db } from "./firebaseConfig";
import { collection, addDoc, serverTimestamp, doc, updateDoc, arrayUnion } from "firebase/firestore";
import Cookies from "js-cookie";

// Components & Pages
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import ScrollToTop from "./components/ScrollToTop";
import Home from "./pages/Home";
import About from "./pages/About";
import Services from "./pages/Services";
import AndroidDev from './pages/services/AndroidDevelopment';
import FlutterDev from './pages/services/FlutterDevelopment';
import ReactNativeDev from './pages/services/ReactNativeDevelopment';
import HybridDev from './pages/services/HybridAppDevelopment';
import WebDev from './pages/services/WebDevelopment';
import ItConsulting from './pages/services/ITConsulting';
import Portfolio from "./pages/Portfolio";
import Contact from "./pages/Contact";
import BlogReader from "./pages/BlogReader";

function App() {
  const location = useLocation();
  const [showBanner, setShowBanner] = useState(false);
  const [userData, setUserData] = useState(null);
  
  const activeUserRefId = useRef(null); // Active users status ke liye
  const currentSessionId = useRef(null); // Analytics doc ID fix karne ke liye
  const heartbeatRef = useRef(null);
  const pageStartRef = useRef(Date.now());

  /* ---------------- 1. LOCATION FETCHING ---------------- */
  const fetchLocation = async () => {
    const cached = Cookies.get("user_location");
    if (cached) return JSON.parse(cached);

    return new Promise((resolve) => {
      if ("geolocation" in navigator) {
        navigator.geolocation.getCurrentPosition(
          async (pos) => {
            try {
              const { latitude, longitude } = pos.coords;
              const res = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`);
              const data = await res.json();
              const addr = data.address;
              const cityName = addr.city || addr.town || addr.village || addr.suburb || "Unknown";
              const loc = {
                city: cityName,
                country: addr.country || "India",
                lat: latitude,
                lon: longitude,
                full: `${cityName}, ${addr.state || ""}`
              };
              Cookies.set("user_location", JSON.stringify(loc), { expires: 1 });
              resolve(loc);
            } catch { resolve(await fetchIPFallback()); }
          },
          async () => resolve(await fetchIPFallback()),
          { timeout: 6000 }
        );
      } else { fetchIPFallback().then(resolve); }
    });
  };

  const fetchIPFallback = async () => {
    try {
      const res = await fetch("https://ipapi.co/json/");
      const data = await res.json();
      const loc = {
        city: data.city || "Unknown",
        country: data.country_name || "India",
        lat: data.latitude,
        lon: data.longitude,
        full: `${data.city || "Unknown"}, ${data.region || ""}`
      };
      Cookies.set("user_location", JSON.stringify(loc), { expires: 1 });
      return loc;
    } catch { return null; }
  };

  /* ---------------- 2. SESSION & ANALYTICS LOGIC ---------------- */
  const initSession = async () => {
    const loc = await fetchLocation();
    if (!loc) return;

    const device = window.innerWidth < 768 ? "Mobile" : window.innerWidth < 1024 ? "Tablet" : "Desktop";
    const payload = { ...loc, device };
    setUserData(payload);

    try {
      // FIX: Yahan hum session start hote hi ek permanent document banayenge
      const sessionRef = await addDoc(collection(db, "analytics"), {
        timestamp: serverTimestamp(),
        location: payload,
        device: device,
        pages: [window.location.pathname], // List of visited pages
        totalDuration: 0,
        type: "session_data"
      });
      
      currentSessionId.current = sessionRef.id; // Is ID ko save kar liya poore session ke liye

      // Active User Tracking (Side panel ke liye)
      const activeRef = await addDoc(collection(db, "active_users"), {
        ...payload,
        page: window.location.pathname,
        joinedAt: serverTimestamp(),
        lastActive: Date.now()
      });
      activeUserRefId.current = activeRef.id;

      // Heartbeat
      heartbeatRef.current = setInterval(async () => {
        if (activeUserRefId.current) {
          try {
            await updateDoc(doc(db, "active_users", activeUserRefId.current), {
              lastActive: Date.now(),
              page: window.location.pathname
            });
          } catch (e) { console.error("Heartbeat failed", e); }
        }
      }, 15000);

      // Tab Close Cleanup
      window.addEventListener("beforeunload", () => {
        if (activeUserRefId.current) {
          const projectId = db.app.options.projectId;
          const url = `https://firestore.googleapis.com/v1/projects/${projectId}/databases/(default)/documents/active_users/${activeUserRefId.current}`;
          fetch(url, { method: 'DELETE', keepalive: true });
        }
      });

    } catch (err) {
      console.error("Firebase Error:", err.message);
    }
  };

  /* ---------------- 3. EFFECTS ---------------- */
  useEffect(() => {
    const consent = Cookies.get("cookie_consent");
    if (!consent) setShowBanner(true);
    else initSession();

    return () => {
      if (heartbeatRef.current) clearInterval(heartbeatRef.current);
    };
  }, []);

  // Page Change Tracking (Bug Fixed Here)
  useEffect(() => {
    if (!userData || !currentSessionId.current) return;

    const updateAnalytics = async () => {
      const duration = Math.round((Date.now() - pageStartRef.current) / 1000);
      
      if (duration >= 0) {
        const docRef = doc(db, "analytics", currentSessionId.current);
        try {
          // Naya doc banane ke bajaye purane doc ko update karein
          await updateDoc(docRef, {
            // Hum pages array mein naya route add kar rahe hain aur total time update kar rahe hain
            pages: arrayUnion(location.pathname), 
            totalDuration: (duration || 0) // Aap isme increment logic bhi laga sakte hain
          });
        } catch (e) {
          console.error("Failed to update analytics:", e);
        }
      }
      pageStartRef.current = Date.now();
    };

    return () => updateAnalytics();
  }, [location.pathname, userData]);

  const handleAccept = () => {
    Cookies.set("cookie_consent", "true", { expires: 365 });
    setShowBanner(false);
    initSession();
  };

  return (
    <ThemeProvider
      theme={createTheme({
        palette: { mode: 'dark', background: { default: '#071024', paper: '#071826' } },
        shape: { borderRadius: 12 },
      })}
    >
      <div className="App">
      <Navbar />
      <ScrollToTop />
      <Box sx={{ minHeight: "80vh" }}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/services" element={<Services />} />
          <Route path="/services/android-development" element={<AndroidDev />} />
          <Route path="/services/flutter-development" element={<FlutterDev />} />
          <Route path="/services/react-native" element={<ReactNativeDev />} />
          <Route path="/services/hybrid-app-development" element={<HybridDev />} />
          <Route path="/services/web-development" element={<WebDev />} />
          <Route path="/services/it-consulting" element={<ItConsulting />} />
          <Route path="/portfolio" element={<Portfolio />} />
          <Route path="/blog" element={<BlogReader />} />
          <Route path="/blog/:id" element={<BlogReader />} />
          <Route path="/contact" element={<Contact />} />
        </Routes>
      </Box>

      <Slide direction="up" in={showBanner} mountOnEnter unmountOnExit>
        <Paper elevation={24} sx={{ position: "fixed", bottom: { xs: 0, md: 20 }, left: { xs: 0, md: 30 }, maxWidth: { md: 450 }, p: 3, borderRadius: { xs: 0, md: 4 }, bgcolor: "#1e293b", color: "white", zIndex: 9999 }}>
          <Stack spacing={2}>
            <Typography variant="h6" sx={{ color: "#00e5ff", fontWeight: "bold" }}>🍪 Cookie Preferences</Typography>
            <Typography variant="body2" sx={{ opacity: 0.85 }}>We use cookies to analyze traffic and improve your experience.</Typography>
            <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
              <Button sx={{ color: "#94a3b8" }} onClick={() => setShowBanner(false)}>Reject</Button>
              <Button variant="contained" onClick={handleAccept} sx={{ bgcolor: "#00e5ff", color: "#0f172a", fontWeight: 600 }}>Accept All</Button>
            </Box>
          </Stack>
        </Paper>
      </Slide>
      <Footer />
      </div>
    </ThemeProvider>
  );
}

export default App;


// import React, { useEffect, useState, useRef } from "react";
// import { Box, Button, Typography, Paper, Slide, Stack } from "@mui/material";
// import { Routes, Route, useLocation } from "react-router-dom";
// import { db } from "./firebaseConfig";
// import { collection, addDoc, serverTimestamp, doc, updateDoc, deleteDoc } from "firebase/firestore";
// import Cookies from "js-cookie";

// // Components & Pages
// import Navbar from "./components/Navbar";
// import Footer from "./components/Footer";
// import ScrollToTop from "./components/ScrollToTop";
// import Home from "./pages/Home";
// import About from "./pages/About";
// import Services from "./pages/Services";
// import Portfolio from "./pages/Portfolio";
// import Contact from "./pages/Contact";

// function App() {
//   const location = useLocation();
//   const [showBanner, setShowBanner] = useState(false);
//   const [userData, setUserData] = useState(null);
//   const activeUserRefId = useRef(null);
//   const heartbeatRef = useRef(null);
//   const pageStartRef = useRef(Date.now());

//   /* ---------------- 1. ADVANCED LOCATION FETCHING ---------------- */
//   const fetchLocation = async () => {
//     const cached = Cookies.get("user_location");
//     if (cached) return JSON.parse(cached);

//     return new Promise((resolve) => {
//       if ("geolocation" in navigator) {
//         navigator.geolocation.getCurrentPosition(
//           async (pos) => {
//             try {
//               const { latitude, longitude } = pos.coords;
//               const res = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`);
//               const data = await res.json();
//               const addr = data.address;

//               // Extracting most accurate city name
//               const cityName = addr.city || addr.town || addr.village || addr.suburb || addr.district || "Unknown";
//               const loc = {
//                 city: cityName,
//                 country: addr.country || "India",
//                 lat: latitude,
//                 lon: longitude,
//                 full: `${cityName}, ${addr.state || addr.region || ""}`
//               };
//               Cookies.set("user_location", JSON.stringify(loc), { expires: 1 });
//               resolve(loc);
//             } catch { resolve(await fetchIPFallback()); }
//           },
//           async () => resolve(await fetchIPFallback()),
//           { timeout: 6000 }
//         );
//       } else { fetchIPFallback().then(resolve); }
//     });
//   };

//   const fetchIPFallback = async () => {
//     try {
//       const res = await fetch("https://ipapi.co/json/");
//       const data = await res.json();
//       const loc = {
//         city: data.city || "Unknown",
//         country: data.country_name || "India",
//         lat: data.latitude,
//         lon: data.longitude,
//         full: `${data.city || "Unknown"}, ${data.region || ""}`
//       };
//       Cookies.set("user_location", JSON.stringify(loc), { expires: 1 });
//       return loc;
//     } catch { return null; }
//   };

//   /* ---------------- 2. SESSION & ANALYTICS LOGIC ---------------- */
//   const initSession = async () => {
//     const loc = await fetchLocation();
//     if (!loc) return;

//     const device = window.innerWidth < 768 ? "Mobile" : window.innerWidth < 1024 ? "Tablet" : "Desktop";
//     const payload = { ...loc, device };
//     setUserData(payload);

//     try {
//       // 1. Create Active User
//       const ref = await addDoc(collection(db, "active_users"), {
//         ...payload,
//         page: window.location.pathname,
//         joinedAt: serverTimestamp(),
//         lastActive: Date.now()
//       });
      
//       activeUserRefId.current = ref.id;

//       // 2. Heartbeat (To keep the session alive)
//       heartbeatRef.current = setInterval(async () => {
//         if (activeUserRefId.current) {
//           try {
//             await updateDoc(doc(db, "active_users", activeUserRefId.current), {
//               lastActive: Date.now(),
//               page: window.location.pathname
//             });
//           } catch (e) { console.error("Heartbeat failed", e); }
//         }
//       }, 15000);

//       // 3. TAB CLOSE CLEANUP (The Secret Sauce 🚀)
//       window.addEventListener("beforeunload", () => {
//         if (activeUserRefId.current) {
//           // Firebase SDK tab close par kaam nahi karta, isliye REST API use karni padegi
//           const projectId = db.app.options.projectId;
//           const url = `https://firestore.googleapis.com/v1/projects/${projectId}/databases/(default)/documents/active_users/${activeUserRefId.current}`;
          
//           // 'fetch' with 'keepalive' browser band hone ke baad bhi request complete karta hai
//           fetch(url, {
//             method: 'DELETE',
//             keepalive: true 
//           });
//         }
//       });

//     } catch (err) {
//       console.error("Firebase Error:", err.message);
//     }
//   };

//   /* ---------------- 3. COOKIE & VISIBILITY EFFECTS ---------------- */
//   useEffect(() => {
//     const consent = Cookies.get("cookie_consent");
//     if (!consent) setShowBanner(true);
//     else initSession();

//     return () => {
//       if (heartbeatRef.current) clearInterval(heartbeatRef.current);
//     };
//   }, []);

//   // Page View Analytics (Route change par log karna)
//   useEffect(() => {
//     if (!userData) return;

//     const logVisit = async () => {
//       const duration = Math.round((Date.now() - pageStartRef.current) / 1000);
//       if (duration > 2) { // Sirf 2 sec se zyada rukne par log karein
//         await addDoc(collection(db, "analytics"), {
//           timestamp: serverTimestamp(),
//           page: location.pathname,
//           device: userData.device,
//           location: userData,
//           duration: duration,
//           type: "view_time"
//         });
//       }
//       pageStartRef.current = Date.now();
//     };

//     return () => logVisit();
//   }, [location.pathname, userData]);

//   const handleAccept = () => {
//     Cookies.set("cookie_consent", "true", { expires: 365 });
//     setShowBanner(false);
//     initSession();
//   };

//   return (
//     <div className="App">
//       <Navbar />
//       <ScrollToTop />
//       <Box sx={{ minHeight: "80vh" }}>
//         <Routes>
//           <Route path="/" element={<Home />} />
//           <Route path="/about" element={<About />} />
//           <Route path="/services" element={<Services />} />
//           <Route path="/portfolio" element={<Portfolio />} />
//           <Route path="/contact" element={<Contact />} />
//         </Routes>
//       </Box>

//       <Slide direction="up" in={showBanner} mountOnEnter unmountOnExit>
//         <Paper elevation={24} sx={{ position: "fixed", bottom: { xs: 0, md: 20 }, left: { xs: 0, md: 30 }, maxWidth: { md: 450 }, p: 3, borderRadius: { xs: 0, md: 4 }, bgcolor: "#1e293b", color: "white", zIndex: 9999 }}>
//           <Stack spacing={2}>
//             <Typography variant="h6" sx={{ color: "#00e5ff", fontWeight: "bold" }}>🍪 Cookie Preferences</Typography>
//             <Typography variant="body2" sx={{ opacity: 0.85 }}>We use cookies to analyze traffic and improve your experience.</Typography>
//             <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
//               <Button sx={{ color: "#94a3b8" }} onClick={() => setShowBanner(false)}>Reject</Button>
//               <Button variant="contained" onClick={handleAccept} sx={{ bgcolor: "#00e5ff", color: "#0f172a", fontWeight: 600 }}>Accept All</Button>
//             </Box>
//           </Stack>
//         </Paper>
//       </Slide>
//       <Footer />
//     </div>
//   );
// }

// export default App;
